
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { PropType } from 'vue'
import type { RelatedPromptsMessages } from '../../models'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from 'vue'
import { mapActions } from '../../composables'
import { ColorEnum, FeatureIdEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { UiTooltip } from '../keen-ui'
import { EpbBadge, EpbButton } from '../shared'

export default defineComponent({
  name: 'RelatedPromptsMetrics',
  components: {
    EpbBadge,
    EpbButton,
    UiTooltip,
  },
  props: {
    messages: {
      type: Object as PropType<RelatedPromptsMessages['metrics']>,
      required: true,
    },
  },
  setup(props) {
    const baseClass = 'related-prompts-metrics'

    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const {
      proxy: { $epb, $router, $store },
    } = ctx

    const { fetchStatsReport } = mapActions($store, ANALYTICS_CONSTS.moduleName, {
      fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
    })

    const { clearFilters } = mapActions($store, CONTEXT_CONSTS.moduleName, {
      clearFilters: CONTEXT_CONSTS.actions.clearFilters,
    })

    const data = ref()

    const formatNumber = (number: number): string => {
      return $epb.utils.formatNumber(number, { maximumFractionDigits: 2 }, true)
    }

    const fetchData = async (
      origins = [
        'related_prompts:results',
        'related_prompts:no_results',
        'related_prompts:low_results',
        'related_prompts:none',
        'overview:none',
        'overview:results',
      ],
    ): Promise<StatsResponse> => {
      const endDate = new Date()
      endDate.setUTCHours(0, 0, 0, 0)
      const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000)

      const response: StatsResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.DAILY_ORIGINS,
        params: {
          origins,
          startDate,
          endDate,
        },
      })
      return response.data
    }

    const kpis = computed(() => [
      {
        label: props.messages.queries,
        value: formatNumber(data.value?.queryCount ?? 0),
        color: ColorEnum.PURPLE_5,
        icon: 'highlights',
        iconColor: ColorEnum.PURPLE_75,
      },
      {
        label: props.messages.clicks,
        value: formatNumber(data.value?.dist.reduce((sum, item) => sum + item?.clickCount, 0) ?? 0),
        color: ColorEnum.GREEN_5,
        icon: 'actions',
        iconColor: ColorEnum.GREEN_75,
      },
      {
        label: props.messages.add2cart,
        value: formatNumber(
          data.value?.dist.reduce((sum, item) => sum + item?.add2cartCount, 0) ?? 0,
        ),
        color: ColorEnum.BLUE_5,
        icon: 'add2cart',
        iconColor: ColorEnum.BLUE_75,
      },
    ])

    const loadData = async (): Promise<void> => {
      data.value = await fetchData()
    }

    const goAnalytics = async () => {
      await clearFilters()
      $router.push({ name: ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_PROMPTS_ANALYTICS] })
    }

    onMounted(() => {
      loadData()
    })

    return {
      baseClass,
      kpis,
      goAnalytics,
    }
  },
})
